import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import { alpha } from "@mui/material/styles";
import RedeemRoundedIcon from "@mui/icons-material/RedeemRounded";
import { useTranslation } from "../../hooks";
import { PortalStatusPill } from "./PortalStatusPill";
import { PortalCard } from "./ui/PortalCard";
import { PortalSection } from "./ui/PortalSection";
import { PortalEmptyState } from "./ui/PortalEmptyState";
import { formatMoney } from "./appointmentPresentation";
function formatDate(value) {
    if (!value)
        return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime()))
        return value;
    return parsed.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}
function packPill(pack) {
    return pack.isExpired ? { labelKey: "packExpired", tone: "neutral" } : null;
}
function SessionsBar({ remaining, total }) {
    if (total <= 0)
        return null;
    const ratio = Math.max(0, Math.min(1, remaining / total));
    return (_jsx(Box, { "aria-hidden": "true", sx: (theme) => ({
            mt: 1,
            height: 5,
            borderRadius: 999,
            overflow: "hidden",
            backgroundColor: alpha(theme.palette.text.secondary, 0.12),
        }), children: _jsx(Box, { sx: (theme) => ({
                height: "100%",
                width: `${ratio * 100}%`,
                borderRadius: 999,
                backgroundColor: remaining > 0
                    ? theme.palette.primary.main
                    : alpha(theme.palette.text.secondary, 0.4),
            }) }) }));
}
export function SessionPacksSection({ packs, loading, showPatientName, currency, }) {
    const { t } = useTranslation("portal");
    return (_jsx(PortalSection, { title: t("yourPacks", "Your session packs"), children: loading ? (_jsxs(Stack, { spacing: 1.25, children: [_jsx(Skeleton, { variant: "rounded", height: 84 }), _jsx(Skeleton, { variant: "rounded", height: 84 })] })) : packs.length === 0 ? (_jsx(PortalCard, { children: _jsx(PortalEmptyState, { icon: _jsx(RedeemRoundedIcon, {}), title: t("noPacks", "You haven't bought a session pack at this clinic.") }) })) : (_jsx(Stack, { spacing: 1.25, children: packs.map((pack) => {
                const remaining = pack.sessionsRemaining ?? 0;
                const total = pack.totalSessions ?? 0;
                const pill = packPill(pack);
                return (_jsxs(PortalCard, { sx: { px: { xs: 1.75, sm: 2 }, py: 1.75, opacity: pack.isExpired ? 0.75 : 1 }, children: [_jsxs(Stack, { direction: "row", spacing: 2, sx: {
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                            }, children: [_jsxs(Box, { sx: { minWidth: 0, flex: 1 }, children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: 700 }, children: pack.sessionPackName || "—" }), _jsx(Typography, { variant: "caption", sx: { color: "text.secondary", display: "block" }, children: t("packSessions", "{{remaining}} of {{total}} sessions left", { remaining, total }) }), _jsx(SessionsBar, { remaining: remaining, total: total })] }), _jsxs(Stack, { spacing: 0.5, sx: { alignItems: "flex-end", flexShrink: 0 }, children: [pill ? _jsx(PortalStatusPill, { pill: pill }) : null, pack.amount != null ? (_jsx(Typography, { variant: "caption", sx: { color: "text.secondary", fontWeight: 600 }, children: formatMoney(pack.amount, currency) })) : null] })] }), showPatientName && pack.patientName ? (_jsx(Typography, { variant: "caption", sx: { color: "text.secondary", display: "block", mt: 1 }, children: t("packFor", "For {{name}}", { name: pack.patientName }) })) : null, pack.expiresOn ? (_jsx(Typography, { variant: "caption", sx: { color: "text.secondary", display: "block", mt: 0.25 }, children: t("walletExpires", "Expires {{date}}", {
                                date: formatDate(pack.expiresOn),
                            }) })) : null] }, pack.id));
            }) })) }));
}
